import { Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { ReactNode } from "react";

type WasteValueDisplayProps = {
  label: string;
  value: number | string;
  unit?: string;
  /** Optional explanation, shown as an info icon beside the label. */
  tooltip?: ReactNode;
};

export function WasteValueDisplay(props: WasteValueDisplayProps) {
  let value = props.value;
  if (isNumber(props.value)) {
    // Full comma-delimited numbers by design — the Waste module deliberately
    // dropped "1.2 K"-style abbreviation so values can be compared digit-for-
    // digit against RCRAInfo reports.
    value = Math.round(props.value).toLocaleString();
  }
  const unit = props.unit ?? "lbs";
  return (
    // A wrapping flex row breaks at each item's flex-basis, so a content-sized
    // basis would push a long-labeled stat onto a new line before its label
    // text ever wraps. Basing the item at min-content keeps the stats row
    // single-line while the numbers fit — the label wraps internally under
    // pressure — and growth capped at max-content restores the natural width
    // when there's room.
    <Stack
      py={1}
      justifyContent="end"
      sx={{ flexGrow: 1, flexShrink: 1, flexBasis: "min-content", maxWidth: "max-content" }}
    >
      {/* Only wrap the label in a row when there's an icon to sit beside it. */}
      {props.tooltip ? (
        <Stack direction="row" alignItems="center" gap={0.25}>
          <Typography fontSize="0.75rem" fontWeight={400} color="textSecondary">
            {props.label}
          </Typography>
          <InfoIconTooltip tooltip={props.tooltip} />
        </Stack>
      ) : (
        <Typography fontSize="0.75rem" fontWeight={400} color="textSecondary">
          {props.label}
        </Typography>
      )}
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography fontSize="2rem" fontWeight={400} color="textPrimary">
          {value}
        </Typography>
        {isNumber(props.value) && (
          <Typography fontSize="0.75rem" fontWeight={400} color="textSecondary">
            {unit}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function isNumber(n: string | number): n is number {
  return typeof n === "number";
}
